'use client';

import queryString from 'query-string';
// This hook is intended for use in Next.js environment only
// and there is a legacy counterpart for this as well
// eslint-disable-next-line import/no-unresolved
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import { ALGOLIA_INDEX_NAME, DEFAULT_SORT_ORDER } from 'commons/constants';
import { CATEGORY_INDEX_MAPPING } from '../../commons/helpers';

const normalizeCategoryParam = raw =>
  CATEGORY_INDEX_MAPPING[raw] || raw || ALGOLIA_INDEX_NAME.PRODUCTS;

const useAdvancedSearchNextURL = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const rawCategoryParam =
    searchParams?.get('category') ?? ALGOLIA_INDEX_NAME.PRODUCTS;
  const categoryUrlParam = normalizeCategoryParam(rawCategoryParam);
  const queryUrlParam = searchParams?.get('q') ?? '';
  const sortByUrlParam = searchParams?.get('sortBy') ?? DEFAULT_SORT_ORDER;

  // Debounced version for typing (to avoid too many URL updates)
  const debouncedUpdateUrlParam = useMemo(
    () =>
      debounce(updates => {
        if (updates) {
          const prev = Object.fromEntries(searchParams);
          const merged = { ...prev, ...updates };
          const updatedUrlParams = queryString.stringify(merged);
          const newPath = `${pathname}${
            !pathname.endsWith('/') ? '/' : ''
          }?${updatedUrlParams}`;
          router.replace(newPath, { scroll: false });
        }
      }, 500),
    [router, pathname, searchParams]
  );

  // Immediate version for programmatic updates (like clear, reset)
  const updateUrlParamImmediate = useCallback(
    updates => {
      if (updates) {
        const prev = Object.fromEntries(searchParams);
        const merged = { ...prev, ...updates };
        const updatedUrlParams = queryString.stringify(merged);
        const newPath = `${pathname}${
          !pathname.endsWith('/') ? '/' : ''
        }?${updatedUrlParams}`;
        router.replace(newPath, { scroll: false });
      }
    },
    [router, pathname, searchParams]
  );

  return {
    pathname,
    queryUrlParam,
    categoryUrlParam,
    sortByUrlParam,
    updateUrlParam: debouncedUpdateUrlParam,
    updateUrlParamImmediate,
  };
};

export default useAdvancedSearchNextURL;
